<template>
	<el-form ref="form" :model="project_budget" :rules="validation_rules" label-position="top">
		<div class="note-card project-budget-card">
			<h1>Project Budget</h1>
			<p>Provide a project budget in the table below.  Funds from the Greenbelt Program may be used for administrative costs and expenses customary and reasonable to the acquisition of property. Note Greenbelt funds cannot be used to pay for property taxes or deed stamps.</p>
			<p>Allowable costs incurred by the applicant during the application process will not be reimbursed if the project is denied.</p>
			<hr/>
			<div v-for="(account, index) in accounts" :key="`${account.label}`" class="budget-record avoid-break">
				<div class="budget-record-header">
					<h2>{{ budget_options[index].title }}</h2>
					<p>{{ budget_options[index].description }}</p>
				</div>
				<div class="budget-record-fieldset">
					<el-form-item label="Greenbelt Funds">
						<el-input v-model="account.greenbelt_funds" v-currency="money" :disabled="!! (account.label == 'Other Improvements' || account.label == 'Other')"/>
					</el-form-item>
					<el-form-item label="Match">
						<el-input v-model="account.match" v-currency="money"/>
					</el-form-item>
					<el-form-item
					label="Total"
					:error="(account.label === 'ESA') ? totalError(index) : null" >
					<el-input disabled :value="getTotal(index)" v-currency="money"/>
				</el-form-item>
			</div>

		</div>
		<div class="budget-record  avoid-break" style="border-top:1px double lightgray;">
			<h2>Grand Totals</h2>
			<div class="budget-record-fieldset">
				<el-form-item label="Greenbelt Funds">
					<el-input disabled :value="getGreenbeltTotal()" v-currency="money"/>
				</el-form-item>
				<el-form-item label="Match">
					<el-input disabled :value="getMatchTotal()" v-currency="money"/>
				</el-form-item>
				<el-form-item el-form-item label="Total">
					<el-input disabled :value="getGrandTotal()" v-currency="money"/>
				</el-form-item>
			</div>
		</div>
		<hr/>
		<el-form-item v-if="getTotal(7) !== '$0.00'" prop="other_description" label="Detail 'Other' expenses here">
			<el-input  v-model="other_description" type="textarea" :autosize="input_config.textarea_autosize"/>
		</el-form-item>
	</div>
	<budgetMatchDetail/>
</el-form>
</template>

<script>
import { mapState } from 'vuex';
import { mapFields, mapMultiRowFields } from 'vuex-map-fields';
import { mapSimpleValidation } from '@/js/utilities/validation-mapper.js';
import validation_mixin from '@/js/mixins/validation-mixin.js';
import budgetMatchDetail from './Budget-Match-Detail';
import money_mixin from '@/js/mixins/money-mixin.js';
import { formatMoney, unformat } from 'accounting-js';
export default {
	components: { budgetMatchDetail },
	mixins: [validation_mixin, money_mixin],
	computed: {
		...mapState('application', ['project_budget']),
		...mapFields('application/project_budget', ['other_description']),
		...mapMultiRowFields('application/project_budget', ['accounts']),

	},
	methods: {
		validateForm(){
			// Custom validation overriding mixin
			let input_fields_valid = null;
			this.$refs.form.validate( is_valid => {
				input_fields_valid = is_valid;
			});

			console.log(input_fields_valid);

			let greenbelt_funds = unformat(this.getGreenbeltTotal());
			let esa_total = unformat(this.getTotal(3));

			if(greenbelt_funds === 0){
				this.$notify.error({
					title: 'Budget Incomplete',
					message: 'Requested greenbelt funds cannot total $0.'
				});
			}

			const success_conditions = (
				input_fields_valid &&
				greenbelt_funds !== 0 &&
				esa_total !== 0
			);
			this.setSectionStatus(success_conditions);
			return success_conditions;
		},
		totalError(index){
			let total = unformat(this.getTotal(index));
			if(total <= 0){
				return 'Must total more than $0.00';
			} else {
				return null;
			}
		},
		getTotal(index){
			const { greenbelt_funds, match } = this.accounts[index]
			return formatMoney(unformat(greenbelt_funds) + unformat(match));
		},
		getGreenbeltTotal(){
			return formatMoney(this.accounts.reduce( (accum, { greenbelt_funds }) => accum += unformat(greenbelt_funds), 0));
		},
		getMatchTotal(){
			return formatMoney(this.accounts.reduce( ( accum, { match }) => accum += unformat(match), 0));
		},
		getGrandTotal(){
			return formatMoney(this.accounts.reduce( ( accum, account, index) => accum += unformat(this.getTotal(index)), 0));
		},
		shouldDisplayGreenbelt(field_title){
			let disallowed_fields = ['Other Improvements', 'Other'];
			return (!disallowed_fields.includes(field_title));
		}
	},
	data(){
		return{
			validation_rules: {
				...mapSimpleValidation(['other_description']),
			},
			budget_options: [
				{
					title: "Land",
					description: "The amount of land match will be the difference between the Greenbelt funds requested and either the purchase price or the appraised value (whichever is larger). Include all applicant contributions (including awarded grant funding, fund balance contribution, etc.) and/or landowner donation value (bargain sale amount) as land purchase match in this section."
				},
				{
					title: "Allowable Minor Improvements",
					description: "Only the following minor improvements may be paid for with Greenbelt Funds – unpaved roadways, unpaved small parking areas, unpaved trails, boardwalks and footbridges."
				},
				{
					title: "Other Planned Improvements",
					description: "Improvements that cannot be funded with Greenbelt funds such as sitework, buildings, restrooms, paving, lighting, etc. These improvements may be considered as match to the project. Proof of dedicated funding may be required."
				},
				{
					title: "Environmental Survey Assessment (ESA)",
					description: "A Phase 1 ESA must be conducted on each greenbelt property prior to closing. The ESA does not need to be completed during the application process."
				},
				{
					title: "Appraisal",
					description: "A full appraisal report less than one year old is required prior to closing and strongly encouraged to be included as part of the application and review process."
				},
				{
					title: "Survey / Plat",
					description: "An approved, recorded plat or boundary survey indicating the subject parcel’s property lines is required prior to closing. Survey/Plat must show actual acreage and boundary lines of the property or portion of the property being funded with Greenbelt monies, unless otherwise approved by the County."
				},
				{
					title: "Closing Costs",
					description: "Eligible costs include items such as reasonable attorney fees, recording fees, title search, etc. Greenbelt funds cannot pay for property taxes or deed stamps. Greenbelt always pays for its portion of title binder and title policy premium separate from the project budget. Do not include those costs here."
				},
				{
					title: "Other",
					description: "Include the total of all other match sources not accounted for above, including any in-kind match, value of existing structures, etc. here. Applicant must detail all ‘Other’ expenses below."
				}
			]
		}
	}
}
</script>

<style lang="scss" scoped>
.budget-record-fieldset{

	display:flex;
	::v-deep{
		.el-input.is-disabled .el-input__inner{
			color: rgb(130,130,132);
		}
		.el-form-item{
			> .el-form-item__label:after{
				display:none;
			}
			&:not(:first-child){
				margin-left:10px;
			}
		}
	}
}
.budget-table ::v-deep .cell{
	word-break: normal;
}
.budget-table ::v-deep h2{
	font-size:1.75rem;
	margin:0;
	padding:0;
}
.budget-table ::v-deep p{
	margin-top:5px;
	margin-bottom:5px;
}
.budget-table ::v-deep .el-table__footer-wrapper{
	font-weight:bold; margin-top:2px;
}

.budget-table ::v-deep .el-table__footer-wrapper tbody tr > td:first-child {
	font-size:1.75rem;
}
.el-table::before {
	display:none;
}

.budget-table ::v-deep  .el-table__body tr.el-table__row--striped td {
	background-color:none !important;
	background:none !important;
}
.budget-table ::v-deep th.is-leaf, .el-table td{
	color:#303133;
	border-bottom: 1px solid #DCDFE6;
}
.budget-table.el-table--enable-row-hover ::v-deep .el-table__body tr:hover > td {
	background-color:#EBEEF5 !important;
	border:none !important;
}
.budget-table ::v-deep .el-table__footer-wrapper tbody tr > td {
	background-color:white !important;
	font-size:1.45rem;
	color:#67C23A;
	border-bottom:none !important;
	border-top:1px solid #DCDFE6 !important;
}
.el-table--group, .el-table--border { border:none !important;}
.budget-table ::v-deep td{border:none !important;border-bottom:1px solid #DCDFE6 !important;}
.budget-table ::v-deep tbody{border:none !important;}

</style>
